import { useState } from 'react';
import { STORAGE_KEY } from '@/constant/constant';
import { noOpenModal, noOpenModalByState } from '../service/api';
import { canShowAnyPop, canShowThisPop, updateFromStorage } from '../service/strategy/priority';
import { formEventV2 } from '../service/formEventV2';
import { getLocal, getSession } from '@/utils/storage';
import { safeJsonParse } from '../utils/safeJsonParse';
import { popupFlowLog } from '@/utils/popupFlowDebug';

/**
 * 模块级：记录各 formId 对应弹窗是否正在展示，用于多弹窗互斥与 noOpenModalByState 校验
 */
const formidState: Record<string, boolean> = {};

/**
 * 弹窗展示前校验：优先级、全局/单弹窗限制、是否已有弹窗打开；通过则更新 show，否则上报 popup_view_stop
 */
export function useInspectShow(props: {
  id: string;
  name: string;
  combinationType?: number;
  triggerEventType: number;
}) {
  /** 当前弹窗是否处于展示状态 */
  const [show, setShow] = useState<boolean>(false);

  const setInspectShow = (value: boolean, from?: string) => {
    /** 当前会话的弹窗配置（用于优先级策略） */
    const config = safeJsonParse(getSession(STORAGE_KEY.POPUP), {});
    updateFromStorage();

    /** 本次展示是否来源于用户点击（点击不拦截） */
    const showFromClick = 'click' === getLocal(STORAGE_KEY.SHOW_FROM) || from === 'click';
    /** 是否为组合弹窗第二步（第二步不拦截）直接展示 */
    const isCombinateType2 = props.combinationType === 2;

    if (value === true && !showFromClick && !isCombinateType2) {
      /** 是否满足全局弹窗展示条件 */
      const isCanShowAnyPop = canShowAnyPop(config);
      /** 是否满足当前单弹窗展示条件 */
      const isCanShowThisPop = canShowThisPop(props, props.id);
      const openModalOk = noOpenModal();
      const openByStateOk = noOpenModalByState(formidState);
      if (isCanShowAnyPop && isCanShowThisPop && openModalOk && openByStateOk) {
        popupFlowLog(
          'inspect-show pass formId=%s triggerEventType=%s from=%s',
          props.id,
          props.triggerEventType,
          from ?? ''
        );
        setShow(value);
        formidState[props.id] = value;
      } else {
        popupFlowLog(
          'inspect-show blocked formId=%s triggerEventType=%s from=%s canAny=%s canThis=%s noOpenModal=%s noOpenModalByState=%s',
          props.id,
          props.triggerEventType,
          from ?? '',
          isCanShowAnyPop,
          isCanShowThisPop,
          openModalOk,
          openByStateOk
        );
        // TODO: 上线前移除调试日志 — 与 popup_view_stop 的 result 四段含义一致
        console.info(
          '[cartx:inspect-show] blocked setShow(true) formId=%s name=%s triggerEventType=%s from=%s canAny=%s canThis=%s noOpenModal=%s noOpenModalByState=%s',
          props.id,
          props.name,
          props.triggerEventType,
          from ?? '',
          isCanShowAnyPop,
          isCanShowThisPop,
          openModalOk,
          openByStateOk
        );
        // 弹窗被优先级/多弹窗互斥等规则拦截时上报，result 为四段条件拼接
        formEventV2({
          event: 'popup_view_stop',
          result: `${isCanShowAnyPop}-${isCanShowThisPop}-${openModalOk}-${openByStateOk}`,
          triggerEventType: props.triggerEventType,
          formId: props.id,
          formName: props.name
        }).catch(() => {});
      }
    } else {
      setShow(value);
      formidState[props.id] = value;
    }
  };

  return [show, setInspectShow] as [boolean, (value: boolean, from?: string) => void];
}
